import { makeAutoObservable, runInAction } from 'mobx';
import {
  adminEmployeePaymentSchemeApplyTemplate,
  adminEmployeePaymentSchemeGet,
  adminEmployeePaymentSchemeUpdate,
  adminEmployeePaymentSchemeUpdateService,
} from '@/services/employeesApi';
import { AppError } from '@/utils/errors';
import type {
  ApplyTemplateResponse,
  PaymentScheme,
  PaymentSchemeRateType,
} from '@/types/employee.types';
import { toastStore } from './ToastStore';

/**
 * Схема оплаты ТЕКУЩЕГО открытого сотрудника (карточка сотрудника).
 * Бэкенд принимает каждое поле/строку отдельным PATCH-запросом — батчевого
 * эндпоинта нет. Но UI копит правки в черновике (см. EmployeeEditPage) и
 * отправляет их одним пакетом (Promise.all) только по кнопке «Сохранить»,
 * а не по мере ввода — см. savePendingChanges.
 */
class EmployeePaymentSchemeStore {
  scheme: PaymentScheme | null = null;
  isLoading = false;
  isSavingFixHourly = false;
  savingServiceUuid: string | null = null;
  isApplyingTemplate = false;
  isSavingDraft = false;
  templatePreview: ApplyTemplateResponse | null = null;

  private employeeUuid: string | null = null;
  private loadRequestId = 0;

  constructor() {
    makeAutoObservable(this);
  }

  reset = () => {
    this.loadRequestId += 1;
    this.scheme = null;
    this.employeeUuid = null;
    this.templatePreview = null;
  };

  loadScheme = async (employeeUuid: string) => {
    this.employeeUuid = employeeUuid;
    this.isLoading = true;
    const requestId = ++this.loadRequestId;
    try {
      const scheme = await adminEmployeePaymentSchemeGet(employeeUuid);
      if (requestId !== this.loadRequestId) return;
      runInAction(() => {
        this.scheme = scheme;
        this.isLoading = false;
      });
    } catch {
      if (requestId !== this.loadRequestId) return;
      runInAction(() => {
        this.isLoading = false;
      });
      toastStore.showError('Не удалось загрузить схему оплаты');
    }
  };

  updateFixAndHourly = async (payload: {
    fixPerShift?: number | null;
    hourlyRate?: number | null;
  }): Promise<boolean> => {
    if (!this.employeeUuid) return false;

    this.isSavingFixHourly = true;
    try {
      const updated = await adminEmployeePaymentSchemeUpdate(this.employeeUuid, {
        ...(payload.fixPerShift !== undefined ? { fix_per_shift: payload.fixPerShift } : {}),
        ...(payload.hourlyRate !== undefined ? { hourly_rate: payload.hourlyRate } : {}),
      });
      runInAction(() => {
        // Инвалидируем ещё летящий loadScheme — иначе его устаревший ответ
        // может перезаписать только что сохранённое значение.
        this.loadRequestId += 1;
        this.scheme = updated;
        this.isSavingFixHourly = false;
      });
      return true;
    } catch {
      runInAction(() => {
        this.isSavingFixHourly = false;
      });
      toastStore.showError('Не удалось сохранить ставку');
      return false;
    }
  };

  updateServiceRate = async (
    serviceUuid: string,
    rateType: PaymentSchemeRateType,
    value: number
  ): Promise<boolean> => {
    if (!this.employeeUuid) return false;

    this.savingServiceUuid = serviceUuid;
    try {
      const updated = await adminEmployeePaymentSchemeUpdateService(this.employeeUuid, serviceUuid, {
        rate_type: rateType,
        value,
      });
      runInAction(() => {
        this.loadRequestId += 1;
        this.scheme = updated;
        this.savingServiceUuid = null;
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.savingServiceUuid = null;
      });
      if (error instanceof AppError && error.statusCode === 400) {
        toastStore.showError(
          rateType === 'percent' ? 'Процент должен быть от 0 до 100' : 'Некорректное значение ставки'
        );
      } else {
        toastStore.showError('Не удалось сохранить ставку по услуге');
      }
      return false;
    }
  };

  previewApplyTemplate = async (templateUuid: string | null, keepOverrides: boolean) => {
    if (!this.employeeUuid) return;

    this.isApplyingTemplate = true;
    try {
      const preview = await adminEmployeePaymentSchemeApplyTemplate(
        this.employeeUuid,
        { template_uuid: templateUuid, keep_overrides: keepOverrides },
        true
      );
      runInAction(() => {
        this.templatePreview = preview;
        this.isApplyingTemplate = false;
      });
    } catch {
      runInAction(() => {
        this.isApplyingTemplate = false;
      });
      toastStore.showError('Не удалось получить превью применения шаблона');
    }
  };

  confirmApplyTemplate = async (templateUuid: string | null, keepOverrides: boolean): Promise<boolean> => {
    if (!this.employeeUuid) return false;

    this.isApplyingTemplate = true;
    try {
      const updated = await adminEmployeePaymentSchemeApplyTemplate(
        this.employeeUuid,
        { template_uuid: templateUuid, keep_overrides: keepOverrides },
        false
      );
      runInAction(() => {
        this.loadRequestId += 1;
        this.scheme = updated;
        this.templatePreview = null;
        this.isApplyingTemplate = false;
      });
      toastStore.showSuccess(templateUuid ? 'Шаблон применён' : 'Сотрудник переведён на ручные ставки');
      return true;
    } catch (error) {
      runInAction(() => {
        this.isApplyingTemplate = false;
      });
      if (error instanceof AppError && (error.statusCode === 404 || error.statusCode === 409)) {
        toastStore.showError(error.message);
      } else {
        toastStore.showError('Не удалось применить шаблон');
      }
      return false;
    }
  };

  clearTemplatePreview = () => {
    this.templatePreview = null;
  };

  /**
   * Отправляет черновик на бэк и возвращает то, что НЕ удалось сохранить
   * (или null, если сохранилось всё). Запросы идут последовательно — бэк не
   * даёт батчевый эндпоинт, а каждый PATCH возвращает полную схему и
   * перезаписывает this.scheme целиком: при параллельной отправке (Promise.all)
   * ответ, пришедший последним, затирал поля, уже обновлённые соседним
   * запросом. Последовательная отправка убирает гонку. Из вернувшегося
   * остатка удалены только успешно сохранённые ключи — повторное
   * «Сохранить» переотправляет лишь то, что реально не прошло.
   */
  savePendingChanges = async (draft: PaymentDraft): Promise<PaymentDraft | null> => {
    const serviceEntries = Object.entries(draft.services);
    const hasFixHourly = draft.fixPerShift !== undefined || draft.hourlyRate !== undefined;
    if (!hasFixHourly && serviceEntries.length === 0) return null;

    this.isSavingDraft = true;
    try {
      const remaining: PaymentDraft = { services: {} };

      if (hasFixHourly) {
        const ok = await this.updateFixAndHourly({
          fixPerShift: draft.fixPerShift,
          hourlyRate: draft.hourlyRate,
        });
        if (!ok) {
          remaining.fixPerShift = draft.fixPerShift;
          remaining.hourlyRate = draft.hourlyRate;
        }
      }

      for (const [serviceUuid, entry] of serviceEntries) {
        const ok = await this.updateServiceRate(serviceUuid, entry.rateType, entry.value);
        if (!ok) {
          remaining.services[serviceUuid] = entry;
        }
      }

      const hasRemaining =
        remaining.fixPerShift !== undefined ||
        remaining.hourlyRate !== undefined ||
        Object.keys(remaining.services).length > 0;
      return hasRemaining ? remaining : null;
    } finally {
      runInAction(() => {
        this.isSavingDraft = false;
      });
    }
  };
}

export interface PaymentDraft {
  fixPerShift?: number | null;
  hourlyRate?: number | null;
  services: Record<string, { rateType: PaymentSchemeRateType; value: number }>;
}

export const employeePaymentSchemeStore = new EmployeePaymentSchemeStore();
export type { EmployeePaymentSchemeStore };
